import React, { useEffect, useState } from 'react';
import { Collapse } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import { SkeletonText } from '../../../Components/Common/Skeleton';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { formatDateToddmmyyhhmm } from '../../../utils/dateformatter';
import { AWARDING_SENDER } from '../utils/awardingHelpers';

const EDITOR_CONFIG = {
  toolbar: [
    'bold',
    'italic',
    'bulletedList',
    'numberedList',
    'link',
    '|',
    'undo',
    'redo',
  ],
};

const stripHtml = value =>
  (value || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

const ResponseSection = ({
  responses = [],
  isLoading = false,
  canRespond = true,
  isSubmitting = false,
  onSubmit,
}) => {
  const [isOpen, setIsOpen] = useState(responses.length > 0);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (responses.length > 0) setIsOpen(true);
  }, [responses.length]);

  const toggle = () => setIsOpen(prev => !prev);

  const openEditor = () => {
    setMessage('');
    setError('');
    setIsEditorOpen(true);
    setIsOpen(true);
  };

  const closeEditor = () => {
    if (isSubmitting) return;
    setIsEditorOpen(false);
    setMessage('');
    setError('');
  };

  const handleSubmit = async () => {
    if (!stripHtml(message)) {
      setError('Response is required');
      return;
    }

    const sent = await onSubmit?.({ message });
    if (sent !== false) {
      setIsEditorOpen(false);
      setMessage('');
    }
  };

  return (
    <div className='border rounded mt-3'>
      <div className='d-flex flex-wrap gap-2 align-items-center justify-content-between p-3'>
        <button
          type='button'
          className='btn btn-link p-0 text-decoration-none text-dark d-flex align-items-center gap-2'
          onClick={toggle}
        >
          <i
            className={
              isOpen
                ? 'ri-arrow-down-s-line fs-20'
                : 'ri-arrow-right-s-line fs-20'
            }
          ></i>
          {responses.length > 0 ? (
            <>
              <span className='badge bg-warning rounded-pill'>
                {responses.length}
              </span>
              <span className='fw-medium'>Response</span>
            </>
          ) : (
            <span className='text-muted'>No responses yet</span>
          )}
        </button>

        <ActionButton
          variant='secondary'
          icon='ri-reply-line'
          onClick={openEditor}
          disabled={!canRespond || isEditorOpen}
        >
          Respond
        </ActionButton>
      </div>

      <Collapse isOpen={isOpen}>
        <div className='px-3 pb-3'>
          {isLoading ? (
            <SkeletonText lines={3} className='py-2' />
          ) : (
            responses.map(response => {
              const isMitra =
                response.senderType === AWARDING_SENDER.companyMitra;

              return (
                <div
                  key={response.id}
                  className={`rounded p-3 mb-2 border-start border-3 ${
                    isMitra
                      ? 'bg-soft-warning border-warning'
                      : 'bg-soft-primary border-primary'
                  }`}
                >
                  <div className='d-flex flex-wrap gap-1 justify-content-between align-items-start mb-1'>
                    <span className='fw-semibold text-dark'>
                      {response.senderName ||
                        (isMitra ? 'Respond Notes' : 'Pertamina Patra Niaga')}
                    </span>
                    <small className='text-muted'>
                      {response.respondDate
                        ? formatDateToddmmyyhhmm(response.respondDate)
                        : ''}
                    </small>
                  </div>
                  <div
                    className='text-dark fs-13 awarding-response-body text-break overflow-auto'
                    // Konten respons dirender sebagai HTML dari editor rich text.
                    dangerouslySetInnerHTML={{ __html: response.message }}
                  />
                </div>
              );
            })
          )}

          {isEditorOpen && (
            <div className='border rounded mt-2'>
              <CKEditor
                editor={ClassicEditor}
                config={EDITOR_CONFIG}
                data={message}
                disabled={isSubmitting}
                onChange={(event, editor) => {
                  setMessage(editor.getData());
                  setError('');
                }}
              />
              {error && (
                <div className='text-danger fs-13 px-3 pt-2'>{error}</div>
              )}
              <div className='d-flex flex-wrap justify-content-end gap-2 p-3 border-top'>
                <ActionButton
                  variant='secondary'
                  icon='ri-close-line'
                  onClick={closeEditor}
                  disabled={isSubmitting}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  variant='primary'
                  icon='ri-send-plane-line'
                  onClick={handleSubmit}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Response'}
                </ActionButton>
              </div>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default ResponseSection;
